import { ViewIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Center,
    HStack, Heading,
    Input,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTransactions } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import { hasPermission, useTableColors } from '../../Utils';

export default function TransactionsPage() {
    const navigate = useNavigate()
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        name: '',
        claimNumber: '',
        startDate: '',
        endDate: ''
    });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const coolors = useTableColors();

    const loadData = async () => {
        setLoading(true);
        const res = await fetchTransactions({ ...filters, page, limit: 50 });
        if (res.success) {
            setData(res.data.transactions);
            setTotalPages(res.data.totalPages);
        }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, [page]);


    const handleSearch = async () => {
        setLoading(true);
        const res = await fetchTransactions({ ...filters, page: 1, limit: 50 });
        if (res.success) {
            setData(res.data.transactions);
            setTotalPages(res.data.totalPages);
            setPage(1); // Reset to first page after search
        }
        setLoading(false);
    };



    if (loading) {
        return <Center mt="20%"><Spinner size="xl" /></Center>;
    }

    if (!hasPermission('viewTransactions')) {
        return <AccessDenied msg={"You don't have permission to view this page"} />
    }


    return (
        <Box>
            <Heading size="lg" mb={4}>All Transactions</Heading>
            <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                bg={coolors.theadBg}
                mb={6}
            >
                <HStack spacing={4} flexWrap="wrap">
                    <Input
                        placeholder="Name"
                        value={filters.name}
                        onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                        size="sm"
                        maxW="200px"
                    />
                    <Input
                        placeholder="Claim Number"
                        value={filters.claimNumber}
                        onChange={(e) => setFilters({ ...filters, claimNumber: e.target.value })}
                        size="sm"
                        maxW="200px"
                    />
                    <Input
                        placeholder="Start Date"
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        size="sm"
                        maxW="160px"
                    />
                    <Input
                        placeholder="End Date"
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        size="sm"
                        maxW="160px"
                    />
                    <Button onClick={handleSearch} size="sm" colorScheme="blue">
                        Search
                    </Button>
                </HStack>
            </Box>


            <Table size="sm" variant="simple">
                <Thead bg={coolors.theadBg}>
                    <Tr>
                        <Th>Name</Th>
                        <Th>Claim Number</Th>
                        <Th>Status</Th>
                        <Th>Total Amount</Th>
                        <Th>Payment Type</Th>
                        <Th>Submitted By</Th>
                        <Th>Submitted At</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {data.map((tx, index) => (
                        <Tr
                            key={tx._id}
                            bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd}
                            color={coolors.textColor}
                            _hover={{ bg: coolors.rowHoverBg, color: "white" }}
                        >
                            <Td>{tx.name}</Td>
                            <Td>{tx.claimNumber}</Td>
                            <Td>
                                <Badge
                                    colorScheme={
                                        tx.otherStatus ? "red" : tx.status === "Success" ? "green" : "red"
                                    }
                                >
                                    {tx.otherStatus || tx.status}
                                </Badge>
                            </Td>
                            <Td>${tx.totalAmount}</Td>
                            <Td>
                                {tx.upcomingCharges?.length > 0
                                    ? "Installment Plan"
                                    : "Full Payment"}
                            </Td>
                            <Td>
                                {tx?.submittedBy === "client" || !tx?.submittedBy
                                    ? tx.initials
                                    : tx?.submittedBy}
                            </Td>
                            <Td>{new Date(tx.createdAt).toLocaleDateString()}</Td>
                            <Td>
                                <Button
                                    size="xs"
                                    colorScheme="green"
                                    onClick={() => navigate(`/transaction/${tx._id}`)}
                                >
                                    <ViewIcon />
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>


            <HStack mt={6} spacing={4}>
                <Button onClick={() => setPage((p) => Math.max(p - 1, 1))} isDisabled={page === 1}>
                    Prev
                </Button>
                <Text>Page {page} of {totalPages}</Text>
                <Button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} isDisabled={page === totalPages}>
                    Next
                </Button>
            </HStack>


        </Box>
    );
}
